import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { LineChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid, Legend } from 'recharts';
import { TrendingUp, TrendingDown, DollarSign, Plus, Edit2, Sliders } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const fmtMoney = (v) => v == null ? '—' : `$${Number(v).toFixed(2)}`;
const fmtPct = (v) => v == null ? '—' : `${v > 0 ? '+' : ''}${v}%`;

const ItemPricingHistoryCard = ({ itemId, vendorId, batchLot }) => {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ unit_cost: '', shipping_cost: '', notes: '' });
  // iter40 Phase B — Y-axis manual scaling + edit dialog
  const [yMode, setYMode] = useState('auto'); // 'auto' | 'manual'
  const [yMin, setYMin] = useState('');
  const [yMax, setYMax] = useState('');
  const [editPoint, setEditPoint] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editBusy, setEditBusy] = useState(false);
  // iter78i — expandable price-history rows showing landed-cost breakdown
  const [expandedRowId, setExpandedRowId] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data } = await api().get(`/items/${itemId}/price-history`);
      setData(data);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (itemId) fetchData(); /* eslint-disable-next-line */ }, [itemId]);

  const chartData = useMemo(() => {
    if (!data) return [];
    return data.points.map(p => ({
      ts: p.recorded_at?.slice(0, 10),
      unit_cost: p.unit_cost,
      shipping_cost: p.shipping_cost,
      landed_unit_cost_usd: p.landed_unit_cost_usd,
    }));
  }, [data]);

  // iter40 Phase B — manual Y-axis bounds. When mode === 'manual' use user inputs;
  // otherwise let recharts auto-scale.
  const yDomain = useMemo(() => {
    if (yMode !== 'manual') return ['auto', 'auto'];
    const lo = yMin === '' ? 'auto' : parseFloat(yMin);
    const hi = yMax === '' ? 'auto' : parseFloat(yMax);
    return [Number.isFinite(lo) ? lo : 'auto', Number.isFinite(hi) ? hi : 'auto'];
  }, [yMode, yMin, yMax]);

  const openEdit = (p) => {
    setEditPoint(p);
    setEditForm({
      unit_cost: p.unit_cost ?? '',
      shipping_cost: p.shipping_cost ?? '',
      landed_unit_cost_usd: p.landed_unit_cost_usd ?? '',
      currency: p.currency || '',
      tariff_cost: p.tariff_cost ?? '',
      customs_cost: p.customs_cost ?? '',
      brokerage_cost: p.brokerage_cost ?? '',
      freight_cost: p.freight_cost ?? '',
      other_barrier_cost: p.other_barrier_cost ?? '',
      other_barrier_label: p.other_barrier_label || '',
      notes: p.notes || '',
    });
  };

  const saveEdit = async () => {
    setEditBusy(true);
    try {
      const payload = Object.fromEntries(
        Object.entries(editForm)
          .filter(([k, v]) => v !== '' && v != null)
          .map(([k, v]) => [k, ['notes', 'currency', 'other_barrier_label'].includes(k) ? v : Number(v)])
      );
      await api().put(`/item-price-history/${editPoint.id}`, payload);
      toast.success('Price point corrected');
      setEditPoint(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    } finally {
      setEditBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    const unit = form.unit_cost === '' ? null : parseFloat(form.unit_cost);
    const ship = form.shipping_cost === '' ? null : parseFloat(form.shipping_cost);
    if (unit == null && ship == null) { toast.error('Provide at least one cost'); return; }
    setSubmitting(true);
    try {
      await api().post(`/items/${itemId}/price-points`, {
        unit_cost: unit,
        shipping_cost: ship,
        vendor_id: vendorId || null,
        batch_lot: batchLot || null,
        source: 'manual',
        notes: form.notes || null,
      });
      toast.success('Price point added');
      setForm({ unit_cost: '', shipping_cost: '', notes: '' });
      setOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add price point');
    } finally {
      setSubmitting(false);
    }
  };

  const summary = data?.summary || {};
  const unitTrend = summary.unit_cost_change_pct;
  const shipTrend = summary.shipping_cost_change_pct;
  const TrendIcon = (v) => (v == null ? null : v > 0 ? TrendingUp : v < 0 ? TrendingDown : null);

  return (
    <Card className="industrial-card" data-testid="item-pricing-history-card">
      <CardHeader className="border-b border-slate-100 pb-3 flex-row items-center justify-between flex">
        <h2 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
          <DollarSign className="w-4 h-4" /> Pricing &amp; Shipping History
        </h2>
        <Button variant="outline" size="sm" onClick={() => setOpen(true)} className="rounded-sm" data-testid="add-price-point-btn">
          <Plus className="w-3 h-3 mr-1" /> Add Price Point
        </Button>
      </CardHeader>
      <CardContent className="p-5 space-y-4">
        {loading ? (
          <div className="text-sm text-slate-500 text-center py-4">Loading…</div>
        ) : !data || data.points.length === 0 ? (
          <div className="text-sm text-slate-500 text-center py-4" data-testid="pricing-history-empty">
            No price points yet. Add one above, or restock with a unit cost to start the history.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div className="border border-slate-200 rounded-sm p-3" data-testid="pricing-summary-unit">
                <div className="text-xs uppercase text-slate-500">Latest Unit Cost</div>
                <div className="text-lg font-bold mono">{fmtMoney(summary.latest_unit_cost)}</div>
                {unitTrend != null && (
                  <div className={`text-xs flex items-center gap-1 mt-0.5 ${unitTrend > 0 ? 'text-red-600' : unitTrend < 0 ? 'text-emerald-600' : 'text-slate-500'}`}>
                    {TrendIcon(unitTrend) && React.createElement(TrendIcon(unitTrend), { className: 'w-3 h-3' })}
                    {fmtPct(unitTrend)} vs first
                  </div>
                )}
              </div>
              <div className="border border-slate-200 rounded-sm p-3" data-testid="pricing-summary-shipping">
                <div className="text-xs uppercase text-slate-500">Latest Shipping</div>
                <div className="text-lg font-bold mono">{fmtMoney(summary.latest_shipping_cost)}</div>
                {shipTrend != null && (
                  <div className={`text-xs flex items-center gap-1 mt-0.5 ${shipTrend > 0 ? 'text-red-600' : shipTrend < 0 ? 'text-emerald-600' : 'text-slate-500'}`}>
                    {TrendIcon(shipTrend) && React.createElement(TrendIcon(shipTrend), { className: 'w-3 h-3' })}
                    {fmtPct(shipTrend)} vs first
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 -mb-2 text-[10px]" data-testid="pricing-axis-controls">
              <Sliders className="w-3 h-3 text-slate-400" />
              <span className="text-slate-500 uppercase tracking-wider">Y-axis:</span>
              <select value={yMode} onChange={(e) => setYMode(e.target.value)} className="border border-slate-200 rounded-sm px-1 py-0.5 text-[10px]" data-testid="pricing-yaxis-mode">
                <option value="auto">Auto</option>
                <option value="manual">Manual</option>
              </select>
              {yMode === 'manual' && (
                <>
                  <Input value={yMin} onChange={(e) => setYMin(e.target.value)} placeholder="min" className="w-14 h-6 mono text-[10px] px-1" data-testid="pricing-yaxis-min" />
                  <Input value={yMax} onChange={(e) => setYMax(e.target.value)} placeholder="max" className="w-14 h-6 mono text-[10px] px-1" data-testid="pricing-yaxis-max" />
                </>
              )}
            </div>
            <div className="h-56" data-testid="pricing-history-chart">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 5, right: 12, left: 0, bottom: 5 }}>
                  <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" />
                  <XAxis dataKey="ts" tick={{ fontSize: 10, fill: '#64748b' }} />
                  <YAxis domain={yDomain} tick={{ fontSize: 10, fill: '#64748b' }} />
                  <Tooltip contentStyle={{ fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line type="monotone" dataKey="unit_cost" stroke="#ea580c" strokeWidth={2} dot={{ r: 3 }} name="Unit cost" />
                  <Line type="monotone" dataKey="shipping_cost" stroke="#0ea5e9" strokeWidth={2} dot={{ r: 3 }} name="Shipping" />
                  <Line type="monotone" dataKey="landed_unit_cost_usd" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} strokeDasharray="4 2" name="Landed (CAD)" />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* iter40 Phase B — editable price points table */}
            <div className="mt-3 max-h-56 overflow-y-auto border border-slate-100 rounded-sm" data-testid="pricing-points-table">
              <table className="w-full text-[11px]">
                <thead className="sticky top-0 bg-slate-50">
                  <tr className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                    <th className="w-4 py-1.5 px-1"></th>
                    <th className="text-left py-1.5 px-2">Date</th>
                    <th className="text-right py-1.5 px-2">Unit</th>
                    <th className="text-right py-1.5 px-2">Ship</th>
                    <th className="text-right py-1.5 px-2">Landed (CAD)</th>
                    <th className="text-left py-1.5 px-2">Source</th>
                    <th className="text-left py-1.5 px-2">Notes</th>
                    <th className="text-right py-1.5 px-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {[...(data.points || [])].reverse().map(p => {
                    const expanded = expandedRowId === p.id;
                    const breakdown = [
                      ['Tariffs', p.tariff_cost],
                      ['Customs / Duty', p.customs_cost],
                      ['Brokerage', p.brokerage_cost],
                      ['Freight', p.freight_cost],
                      ['GST (recoverable, excl)', p.gst_recoverable_cost],
                      ['GST (non-recoverable)', p.gst_non_recoverable_cost],
                      ['Insurance', p.insurance_cost],
                      ['Handling', p.handling_cost],
                      [p.other_barrier_label ? `Other (${p.other_barrier_label})` : 'Other (legacy)', p.other_barrier_cost],
                    ].filter(([, v]) => v != null && Number(v) !== 0);
                    const otherCosts = Array.isArray(p.other_costs) ? p.other_costs.filter((o) => Number(o.value) !== 0) : [];
                    const hasAnyBreakdown = breakdown.length > 0 || otherCosts.length > 0;
                    return (
                      <React.Fragment key={p.id}>
                        <tr className="border-b border-slate-100 hover:bg-slate-50" data-testid={`pricing-point-row-${p.id}`}>
                          <td className="py-1 px-1 text-center">
                            {hasAnyBreakdown ? (
                              <button
                                type="button"
                                onClick={() => setExpandedRowId(expanded ? null : p.id)}
                                className="text-slate-400 hover:text-slate-700 text-xs font-bold"
                                data-testid={`pricing-expand-${p.id}`}
                                title={expanded ? 'Hide breakdown' : 'Show landed-cost breakdown'}
                              >{expanded ? '▾' : '▸'}</button>
                            ) : <span className="text-slate-200">·</span>}
                          </td>
                          <td className="py-1 px-2 mono">{p.recorded_at?.slice(0, 10)}</td>
                          <td className="py-1 px-2 text-right mono">{fmtMoney(p.unit_cost)}</td>
                          <td className="py-1 px-2 text-right mono">{fmtMoney(p.shipping_cost)}</td>
                          <td className="py-1 px-2 text-right mono text-emerald-700">{fmtMoney(p.landed_unit_cost_usd)}</td>
                          <td className="py-1 px-2 text-slate-500">{p.source || '—'}{p.edited_by_name && <span className="text-[9px] italic text-amber-600 ml-1">edited</span>}</td>
                          <td className="py-1 px-2 truncate max-w-[180px]">{p.notes || '—'}</td>
                          <td className="py-1 px-2 text-right">
                            <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => openEdit(p)} data-testid={`pricing-edit-btn-${p.id}`}>
                              <Edit2 className="w-3 h-3" />
                            </Button>
                          </td>
                        </tr>
                        {expanded && hasAnyBreakdown && (
                          <tr className="bg-slate-50/70 border-b border-slate-200" data-testid={`pricing-breakdown-${p.id}`}>
                            <td colSpan={8} className="py-2 px-4">
                              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">
                                Landed cost breakdown {p.quantity ? `(qty ${p.quantity})` : ''}
                                {p.currency && p.currency !== 'CAD' && <span className="ml-2 text-slate-400 normal-case">· {p.currency} → CAD @ {p.fx_rate || 1}</span>}
                              </div>
                              <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-0.5 text-[11px]">
                                {breakdown.map(([label, v]) => (
                                  <div key={label} className="flex justify-between">
                                    <span className="text-slate-600">{label}</span>
                                    <span className="mono">{fmtMoney(v)}</span>
                                  </div>
                                ))}
                                {otherCosts.map((o, i) => (
                                  <div key={`oc-${i}`} className="flex justify-between">
                                    <span className="text-slate-600">{o.label || 'Other'}</span>
                                    <span className="mono">{fmtMoney(o.value)}</span>
                                  </div>
                                ))}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-sm" data-testid="add-price-point-dialog">
          <DialogHeader><DialogTitle>Add Price Point</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Unit Cost</Label>
                <Input
                  type="number" step="0.01" placeholder="0.00"
                  value={form.unit_cost}
                  onChange={(e) => setForm({ ...form, unit_cost: e.target.value })}
                  className="rounded-sm mono"
                  data-testid="price-point-unit-cost"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Shipping Cost</Label>
                <Input
                  type="number" step="0.01" placeholder="0.00"
                  value={form.shipping_cost}
                  onChange={(e) => setForm({ ...form, shipping_cost: e.target.value })}
                  className="rounded-sm mono"
                  data-testid="price-point-shipping-cost"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Notes</Label>
              <Input
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="PO #, vendor quote ref…"
                className="rounded-sm"
                data-testid="price-point-notes"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
              <Button type="submit" disabled={submitting} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="price-point-submit-btn">
                {submitting ? 'Saving…' : 'Save'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* iter40 Phase B — Edit price point dialog */}
      <Dialog open={!!editPoint} onOpenChange={(o) => !o && setEditPoint(null)}>
        <DialogContent className="sm:max-w-md" data-testid="edit-price-point-dialog">
          <DialogHeader><DialogTitle>Correct Price Point</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-xs text-slate-500">
              Use this to fix typos in vendor invoices, FX corrections, or to retroactively add barrier costs.
            </p>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-xs">Unit Cost</Label>
                <Input type="number" step="0.01" value={editForm.unit_cost ?? ''}
                  onChange={(e) => setEditForm({ ...editForm, unit_cost: e.target.value })}
                  className="rounded-sm mono h-8 text-xs" data-testid="edit-unit-cost" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Shipping</Label>
                <Input type="number" step="0.01" value={editForm.shipping_cost ?? ''}
                  onChange={(e) => setEditForm({ ...editForm, shipping_cost: e.target.value })}
                  className="rounded-sm mono h-8 text-xs" data-testid="edit-shipping-cost" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Tariffs</Label>
                <Input type="number" step="0.01" value={editForm.tariff_cost ?? ''}
                  onChange={(e) => setEditForm({ ...editForm, tariff_cost: e.target.value })}
                  className="rounded-sm mono h-8 text-xs" data-testid="edit-tariff" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Customs</Label>
                <Input type="number" step="0.01" value={editForm.customs_cost ?? ''}
                  onChange={(e) => setEditForm({ ...editForm, customs_cost: e.target.value })}
                  className="rounded-sm mono h-8 text-xs" data-testid="edit-customs" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Brokerage</Label>
                <Input type="number" step="0.01" value={editForm.brokerage_cost ?? ''}
                  onChange={(e) => setEditForm({ ...editForm, brokerage_cost: e.target.value })}
                  className="rounded-sm mono h-8 text-xs" data-testid="edit-brokerage" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Freight</Label>
                <Input type="number" step="0.01" value={editForm.freight_cost ?? ''}
                  onChange={(e) => setEditForm({ ...editForm, freight_cost: e.target.value })}
                  className="rounded-sm mono h-8 text-xs" data-testid="edit-freight" />
              </div>
              <div className="space-y-1 col-span-2">
                <Label className="text-xs">Landed Unit Cost (USD)</Label>
                <Input type="number" step="0.0001" value={editForm.landed_unit_cost_usd ?? ''}
                  onChange={(e) => setEditForm({ ...editForm, landed_unit_cost_usd: e.target.value })}
                  className="rounded-sm mono h-8 text-xs" data-testid="edit-landed" />
              </div>
              <div className="space-y-1 col-span-2">
                <Label className="text-xs">Notes</Label>
                <Input value={editForm.notes ?? ''}
                  onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                  placeholder="Reason for correction"
                  className="rounded-sm h-8 text-xs" data-testid="edit-notes" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setEditPoint(null)}>Cancel</Button>
            <Button type="button" disabled={editBusy} onClick={saveEdit} className="bg-amber-600 hover:bg-amber-700" data-testid="edit-price-point-save">
              {editBusy ? 'Saving…' : 'Apply correction'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default ItemPricingHistoryCard;
